import { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import PcOnlyPage from "./pages/pc-only/page";
import { supabase } from "./lib/supabase";

const PC_RESTRICTION_KEY = "charamap_pc_restriction";

function isPcDevice(): boolean {
  const ua = navigator.userAgent;

  // 1. Definite mobile UA — always mobile
  const isMobileUA = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|CriOS|FxiOS/i.test(ua);
  if (isMobileUA) return false;

  // 2. iPad — treat as mobile (modern iPads report "Macintosh" in UA!)
  //    Detect via touch points: iPad has maxTouchPoints >= 5
  const isIPad =
    /iPad/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints >= 5);
  if (isIPad) return false;

  // 3. Small physical screen — treat as mobile
  const minDim = Math.min(window.screen.width, window.screen.height);
  if (minDim <= 768) return false;

  // 4. Desktop Safari on Mac: Macintosh + no touch
  //    (already covered by step 2 for iPad, so here it's a real Mac)
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints === 0) return true;

  // 5. Desktop UA patterns — Windows, Linux desktop
  const isDesktopUA = /Windows NT|Linux x86_64|Linux i686|Linux aarch64/i.test(ua);
  if (isDesktopUA) return true;

  // 6. Fallback: no touch at all → PC
  return navigator.maxTouchPoints === 0;
}

function App() {
  const [pcRestriction, setPcRestriction] = useState<boolean | null>(null);
  const isAdmin = window.location.pathname.includes("/admin");

  useEffect(() => {
    if (isAdmin) {
      setPcRestriction(false);
      return;
    }
    // Load from Supabase first (cloud setting), fallback to localStorage
    supabase
      .from("site_settings")
      .select("value")
      .eq("key", "pc_restriction")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value !== undefined) {
          const enabled = data.value === "true";
          localStorage.setItem(PC_RESTRICTION_KEY, String(enabled));
          setPcRestriction(enabled);
        } else {
          const val = localStorage.getItem(PC_RESTRICTION_KEY);
          setPcRestriction(val === "true");
        }
      })
      .catch(() => {
        const val = localStorage.getItem(PC_RESTRICTION_KEY);
        setPcRestriction(val === "true");
      });
  }, [isAdmin]);

  // While loading restriction setting, show nothing (or cached value)
  if (pcRestriction === null) {
    const cached = localStorage.getItem(PC_RESTRICTION_KEY) === "true";
    if (!isAdmin && isPcDevice() && cached) {
      return <PcOnlyPage />;
    }
    // Show app while loading (avoids flash)
    return (
      <I18nextProvider i18n={i18n}>
        <BrowserRouter basename={__BASE_PATH__}>
          <AppRoutes />
        </BrowserRouter>
      </I18nextProvider>
    );
  }

  // If PC restriction is ON and user is on PC, show PC-only page
  if (!isAdmin && isPcDevice() && pcRestriction) {
    return <PcOnlyPage />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AppRoutes />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
